import { SQLiteDBConnection } from "@capacitor-community/sqlite";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { store } from '../store/stencil-redux';
import bboxPolygon from "@turf/bbox-polygon";
import write_blob from "capacitor-blob-writer";
import EventEmitter from 'eventemitter3';
import { BBox } from "geojson";
import * as mapboxgl from 'mapbox-gl/esm';
import { RequestTransformFunction } from 'mapbox-gl/esm';
import PQueue from "p-queue";
import xyz from 'xyz-affair';
import { CONFIGS } from "../config";
import { initialStore } from "../store";
import { offlineMapsRetry } from "../store/offlineMaps/offlineMaps.actions";
import { getAppConfigMapsUrl, getOfflineMapsMaps } from "../store/selectors";
import { Unthunk } from "../types";
import { OfflineMap, OfflineMapStatus } from "../types/offlineMaps.types";
import { AnalyticsService } from "./analytics.service";
import { BLUESKY_MIN_ZOOM, buildBlueSkyWmsStaticUrl, xyzTileToEpsg3857Bbox } from "./bluesky-wms";
import { mkdirSafe } from "./db/filesystem.service";
import SQLDatabase from "./db/sql.service";
import { delay, getGlobalMaskLayer } from "./utils.service";

class TileNotFoundError extends Error {

}

class MapDownloadError extends Error {

}

/** New BlueSky packs go as deep as BlueSky online, so they are as sharp offline. */
export const BLUESKY_PACK_MAX_ZOOM = 20;

/**
 * Pack max zoom. Mapbox packs stay at 16. New BlueSky packs store their max zoom.
 * Older BlueSky packs don't, and were downloaded 4 levels deeper than the view they
 * were created at, capped at 20. `minZoom` is Mapbox's 512px zoom, and BlueSky tiles
 * are 256px, so that view is already one tile level deeper (+1).
 */
export const getOfflinePackMaxZoom = (map: OfflineMap): number => {
  if (map.styleId === 'bluesky') {
    return map.maxZoom ?? Math.min(20, Math.max(17, Math.floor(map.minZoom) + 1 + 4));
  }
  return 16;
};

const getOfflinePackMinZoom = (map: OfflineMap): number => {
  return map.styleId === 'bluesky' ? BLUESKY_MIN_ZOOM : 0;
};

/** The maps proxy allows 600 requests per 10s per IP; stay well under it. */
const BLUESKY_REQUESTS_PER_SECOND = 40;

/** BlueSky tiles take about a second each upstream, so keep more in flight than for Mapbox. */
const BLUESKY_NATIVE_CONCURRENCY = 8;

/**
 * Waits before each retry of a tile. BlueSky build tiles they haven't cached on demand,
 * which can outlast the maps proxy (504); by the next attempt the tile is usually cached.
 */
const TILE_RETRY_DELAYS_MS = [1000, 3000, 8000];

/**
 * Waits before each retry of a rate-limited tile when the response doesn't say how long
 * to wait. Mapbox limit per minute, so back off for longer than for a slow tile.
 */
const RATE_LIMIT_RETRY_DELAYS_MS = [5000, 20000, 60000];

/** Longest we honour a rate-limit header for, so a bad value can't stall a download. */
const MAX_RATE_LIMIT_DELAY_MS = 60000;

/** Rate limiting and server errors are worth retrying; other client errors are not. */
const isRetryableTileStatus = (status: number): boolean => status === 429 || status >= 500;

/**
 * How long a 429 asks us to wait, from `Retry-After` (seconds or an HTTP date; sent by the
 * maps proxy) or Mapbox's `X-Rate-Limit-Reset` (Unix seconds). Null when neither is
 * readable, e.g. a cross-origin response that doesn't expose them.
 */
export const getRateLimitDelayMs = (headers: Headers, now = Date.now()): number | null => {
  let delayMs: number | null = null;
  const retryAfter = headers.get('Retry-After');
  const reset = headers.get('X-Rate-Limit-Reset');
  if (retryAfter && /^\d+$/.test(retryAfter.trim())) {
    delayMs = Number(retryAfter) * 1000;
  } else if (retryAfter && !Number.isNaN(Date.parse(retryAfter))) {
    delayMs = Date.parse(retryAfter) - now;
  } else if (reset && /^\d+$/.test(reset.trim())) {
    delayMs = Number(reset) * 1000 - now;
  }
  if (delayMs === null) {
    return null;
  }
  return Math.min(Math.max(delayMs, 1000), MAX_RATE_LIMIT_DELAY_MS);
};

type Point = [number, number];
type Bounds = [Point, Point];
const convertBoundsToCorners = (bounds: Bounds): Point[] => {
  const [sw, ne] = bounds;

  const [minLng, minLat] = sw;
  const [maxLng, maxLat] = ne;

  // Southwest corner (already given)
  const southwest: Point = [minLng, minLat];

  // Northwest corner
  const northwest: Point = [minLng, maxLat];

  // Northeast corner (already given)
  const northeast: Point = [maxLng, maxLat];

  // Southeast corner
  const southeast: Point = [maxLng, minLat];

  // return [southwest, northwest, northeast, southeast];
  return [northwest, northeast, southeast, southwest];
}

export type OfflineMapsEventTypes = {
  /** A dynamic pack's whole-number percent of tiles done; only sent when it changes. */
  progress: (uuid: string, percent: number) => void;
};

export interface OfflineMapStyle {
  name: string;
  id: string;
  url: string;
}

export class OfflineMapsService {

  public static instance: OfflineMapsService = new OfflineMapsService();

  private db: SQLiteDBConnection;
  private isNative: boolean = Capacitor.isNativePlatform();

  offlineMapsRetry: Unthunk<typeof offlineMapsRetry>;

  public events = new EventEmitter<OfflineMapsEventTypes>();

  /** Packs with a download running, so a second resume doesn't start them again. */
  private downloading = new Set<string>();

  /** Latest percent per dynamic pack downloading, so a page opened mid-download can show it. */
  private downloadPercents = new Map<string, number>();

  private constructor() {
    store.mapDispatchToProps(this, {
      offlineMapsRetry
    });
  };

  public async init() {
    this.db = await SQLDatabase.instance.createConnection('offline-tiles');
    await this.db.open();
    // (window as any).tileDb = this.db;

    await this.db.execute(`CREATE TABLE IF NOT EXISTS tiles (
      z INTEGER NOT NULL,
      x INTEGER NOT NULL,
      y INTEGER NOT NULL,
      size INTEGER NOT NULL,
      mapUuid TEXT
    )`);

    await this.db.execute(`CREATE INDEX IF NOT EXISTS tiles_uuid ON tiles (
      mapUuid
    )`);

    void this.resumeDownloads();
  }

  /**
   * Resume packs left downloading. Runs at startup and again once a project loads, because
   * premium basemap packs wait for the project's plan features before resuming.
   */
  public async resumeDownloads() {
    const maps = getOfflineMapsMaps(initialStore.getState()).filter(s => s.status === OfflineMapStatus.DOWNLOADING);
    for (const map of maps) {
      if (this.downloading.has(map.uuid)) {
        continue;
      }
      await this.offlineMapsRetry(map.uuid);
    }
  }

  async downloadMap(map: OfflineMap): Promise<number> {
    this.downloading.add(map.uuid);
    try {
      await this.prepareDownload(map);
      if (map.type === 'vector') {
        return await this.downloadVectorMap(map);
      }
      return await this.downloadRasterMap(map);
    } finally {
      this.downloading.delete(map.uuid);
      this.downloadPercents.delete(map.uuid);
    }
  }

  /** Undefined until a dynamic pack's download has started. */
  public getDownloadPercent(uuid: string): number | undefined {
    return this.downloadPercents.get(uuid);
  }

  private setDownloadPercent(uuid: string, percent: number) {
    if (this.downloadPercents.get(uuid) === percent) {
      return;
    }
    this.downloadPercents.set(uuid, percent);
    this.events.emit('progress', uuid, percent);
  }

  async downloadRasterMap(map: OfflineMap) {
    if (map.styleId === 'bluesky') {
      throw new MapDownloadError();
    }
    const result = await this.getRasterImage(map);
    return result.size;
  }

  async downloadVectorMap(map: OfflineMap) {
    const { bounds } = map;
    const tiles = xyz(bounds, getOfflinePackMinZoom(map), getOfflinePackMaxZoom(map));

    AnalyticsService.instance.logEvent('offlineMapDownload', {
      tiles: tiles.length
    });

    if (tiles.length === 0) {
      return 0;
    }

    // Create the parent directory
    await this.mkdir(map);

    let completed = 0;
    this.setDownloadPercent(map.uuid, 0);

    const queue = new PQueue({
      concurrency: this.isNative ? (map.styleId === 'bluesky' ? BLUESKY_NATIVE_CONCURRENCY : 4) : 1,
      interval: 1000,
      intervalCap: map.styleId === 'bluesky' ? BLUESKY_REQUESTS_PER_SECOND : 1000
    });

    const tasks = tiles.map(tile => queue.add(async () => {
      const { z, x, y } = tile;

      const blob = await this.getTile(map, z, x, y);
      if (blob) {
        await OfflineMapsService.instance.writeOfflineTileRecord(z, x, y, blob.size, map.uuid);
      }
      completed += 1;
      this.setDownloadPercent(map.uuid, Math.floor((completed / tiles.length) * 100));
      return blob?.size ?? 0;
    }));

    try {
      const result = await Promise.all(tasks);
      const size = result.reduce((acc, s) => acc + s, 0);
      return size;
    } catch (e) {
      console.warn(e);
      // Drop queued tiles and let in-flight ones finish, so a Retry can't overlap this run.
      queue.clear();
      await queue.onIdle();
      throw new MapDownloadError();
    }
  }

  /**
   * Fetch a tile's body, retrying rate limiting, server errors and network failures
   * before giving up. The body is read inside each attempt, because a connection can
   * drop after a successful response's headers have arrived. A rate-limited tile waits
   * as long as the response asks, or longer than other retries if it doesn't say.
   */
  private async fetchTile(url: string): Promise<{ status: number; blob: Blob | null }> {
    for (let attempt = 0; ; attempt++) {
      let retryDelay = TILE_RETRY_DELAYS_MS[attempt];
      try {
        const resp = await fetch(url);
        if (resp.ok) {
          return { status: resp.status, blob: await resp.blob() };
        }
        if (!isRetryableTileStatus(resp.status) || retryDelay === undefined) {
          return { status: resp.status, blob: null };
        }
        if (resp.status === 429) {
          retryDelay = getRateLimitDelayMs(resp.headers) ?? RATE_LIMIT_RETRY_DELAYS_MS[attempt];
        }
      } catch (e) {
        if (retryDelay === undefined) {
          throw e;
        }
      }
      await delay(retryDelay);
    }
  }

  /** Null when BlueSky has no imagery for the tile; the pack just leaves it empty. */
  private async getTile(map: OfflineMap, z: number, x: number, y: number): Promise<Blob | null> {
    let url: string;
    if (map.styleId === 'bluesky') {
      // Proxy forces WIDTH/HEIGHT=256 (GWC grid); client only sends BBOX.
      const mapsUrl = getAppConfigMapsUrl(store.getState());
      url = buildBlueSkyWmsStaticUrl(mapsUrl, xyzTileToEpsg3857Bbox(z, x, y));
    } else {
      url = `https://api.mapbox.com/styles/v1/mapbox/${map.styleId}/tiles/${z}/${x}/${y}?access_token=${CONFIGS.prod.MAPBOX_TOKEN}`;
    }
    const { status, blob } = await this.fetchTile(url);
    if (!blob) {
      // A client error other than rate limiting means this tile is outside BlueSky's coverage.
      if (map.styleId === 'bluesky' && status >= 400 && status < 500 && status !== 429) {
        return null;
      }
      throw new TileNotFoundError();
    }

    const dir = `/${z}/${x}`;
    await this.mkdir(map, dir);

    await this.writeFile(map, `${dir}/${y}`, blob);

    return blob;
  }

  private async getRasterImage(map: OfflineMap): Promise<Blob> {

    // Create some bounds
    const sw = new mapboxgl.LngLat(map.bounds[0][0], map.bounds[0][1]);
    const ne = new mapboxgl.LngLat(map.bounds[1][0], map.bounds[1][1]);

    const minLon = sw.lng;
    const minLat = sw.lat;
    const maxLon = ne.lng;
    const maxLat = ne.lat;

    const lonLatToMercator = (lon: number, lat: number): { x: number; y: number } => {
      const R_MAJOR = 6378137.0;
      const x = (R_MAJOR * lon * Math.PI) / 180;
      let y = R_MAJOR * Math.log(Math.tan((Math.PI / 4) + (lat * Math.PI) / 360));
      return { x, y };
    }

    const swMercator = lonLatToMercator(minLon, minLat);
    const neMercator = lonLatToMercator(maxLon, maxLat);

    const widthMeters = neMercator.x - swMercator.x;
    const heightMeters = neMercator.y - swMercator.y;

    const aspectRatio = widthMeters / heightMeters;
    const maxDimension = 1280;
    let adjustedWidth: number;
    let adjustedHeight: number;

    if (aspectRatio >= 1) {
      adjustedWidth = maxDimension;
      adjustedHeight = Math.round(adjustedWidth / aspectRatio);
    } else {
      adjustedHeight = maxDimension;
      adjustedWidth = Math.round(adjustedHeight * aspectRatio);
    }
    adjustedWidth = Math.min(Math.max(1, adjustedWidth), maxDimension);
    adjustedHeight = Math.min(Math.max(1, adjustedHeight), maxDimension);
    const bbox = `${minLon},${minLat},${maxLon},${maxLat}`;

    const url = `https://api.mapbox.com/styles/v1/mapbox/${map.styleId}/static/[${bbox}]/${adjustedWidth}x${adjustedHeight}@2x?access_token=${CONFIGS.prod.MAPBOX_TOKEN}`;
    const resp = await fetch(url);
    if (!resp.ok) {
      throw new Error('Error fetching raster image');
    }
    const blob = await resp.blob();
    await this.mkdir(map);
    await this.writeFile(map, '/raster.png', blob);
    return blob;
  }


  public async getOfflineMap(map: OfflineMap): Promise<mapboxgl.StyleSpecification> {

    const uriResult = await Filesystem.getUri({
      directory: Directory.Data,
      path: this.getStoragePath(map)
    });

    const tileUri = map.type === 'vector' ? `${uriResult.uri}/{z}/{x}/{y}` : `${uriResult.uri}/raster.png`;

    const boundsPolygon = bboxPolygon(map.bounds.flat() as BBox);
    const data = getGlobalMaskLayer(boundsPolygon.geometry);

    const offlineStyle: mapboxgl.StyleSpecification = {
      version: 8,
      name: 'Offline Map',
      glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
      sources: {
        'offline-bounds': {
          type: 'geojson',
          data
        }
      },
      layers: [{
        'id': 'offline-mask',
        'type': 'fill',
        'source': 'offline-bounds',
        'paint': {
          'fill-color': '#fff'
        }
      }, {
        'id': 'offline-bounds',
        'type': 'line',
        'source': 'offline-bounds',
        'layout': {
          'line-join': 'bevel'
        },
        'paint': {
          'line-color': '#8833FF',
          'line-width': 2,
          'line-dasharray': [2, 2],
        }
      }]
    };

    if (map.type === 'vector') {
      // Mapbox style tiles are 512; BlueSky GWC tiles are 256.
      const tileSize = map.styleId === 'bluesky' ? 256 : 512;
      const maxZoom = getOfflinePackMaxZoom(map);
      offlineStyle.sources['offline-raster-tiles'] = {
        type: 'raster',
        tiles: [tileUri],
        tileSize,
        minzoom: getOfflinePackMinZoom(map),
        maxzoom: maxZoom,
        bounds: map.bounds.flat() as [number, number, number, number]
      };
      offlineStyle.layers.unshift({
        'id': 'offline-tiles',
        'type': 'raster',
        'source': 'offline-raster-tiles',
        'minzoom': 0,
        'maxzoom': 22,
      });
    } else {
      offlineStyle.sources['offline-raster-tiles'] = {
        type: 'image',
        url: tileUri,
        coordinates: convertBoundsToCorners(map.bounds as Bounds) as [[number, number], [number, number], [number, number], [number, number]]
      };
      offlineStyle.layers.unshift({
        'id': 'offline-raster',
        'type': 'raster',
        'source': 'offline-raster-tiles',
        'paint': {
          'raster-fade-duration': 0
        }
      });
    }

    return offlineStyle;
  }

  public async writeOfflineTileRecord(z: number, x: number, y: number, size: number, mapUuid: string = null): Promise<void> {
    await this.db.executeSet([{
      statement: 'INSERT OR REPLACE INTO tiles (z,x,y,size,mapUuid) VALUES (?,?,?,?,?)',
      values: [
        z,
        x,
        y,
        size,
        mapUuid
      ]
    }]);
    if (!this.isNative) {
      await this.db.close();
      await this.db.open();
    }
  }

  public async deleteOfflineMapTiles(uuid: string): Promise<void> {
    const statement = uuid === null ? 'DELETE FROM tiles WHERE mapUuid IS NULL' : 'DELETE FROM tiles WHERE mapUuid = ?';
    const values = uuid === null ? [] : [uuid];

    await this.db.executeSet([{
      statement,
      values
    }]);

    if (!this.isNative) {
      await this.db.close();
      await this.db.open();
    }
  }

  public async getAmbientTileSize(): Promise<number> {
    const result = await this.db.query('SELECT SUM(size) as total FROM tiles WHERE mapUuid IS NULL', []);
    return result?.values?.[0]?.total ?? 0 as number;
  }

  protected getStoragePath(map: OfflineMap) {
    return `/${map.uuid}`;
  }

  /** Remove leftover pack files/tile rows so Retry (and resume) start clean. */
  private async prepareDownload(map: OfflineMap) {
    try {
      await this.destroy(map);
    } catch {
      // Missing directory is fine on first download or after a prior wipe.
    }
    await this.deleteOfflineMapTiles(map.uuid);
  }

  protected mkdir(map: OfflineMap, path: string = '') {
    return mkdirSafe(`${this.getStoragePath(map)}${path}`, Directory.Data);
  }

  protected async writeFile(map: OfflineMap, path: string, blob: Blob) {
    const writePath = `${this.getStoragePath(map)}${path}`;

    await write_blob({
      directory: Directory.Data,
      path: writePath,
      fast_mode: true,
      blob
    });
  }

  public async deleteMap(map: OfflineMap) {
    await this.destroy(map);
    await this.deleteOfflineMapTiles(map.uuid);
  }

  protected destroy(map: OfflineMap) {
    return Filesystem.rmdir({
      directory: Directory.Data,
      path: this.getStoragePath(map),
      recursive: true
    });
  }

  offlineMapTransformRequestFunction: RequestTransformFunction = (url, type) => {
    // URL Transofmration of offline tiles
    if (type === 'Tile' && url.startsWith('file://')) {
      const result = Capacitor.convertFileSrc(url);
      return {
        url: result
      };
    }

    if (type === 'Image' && url.startsWith('file://')) {
      const result = Capacitor.convertFileSrc(url);
      return {
        url: result
      };
    }

    return {
      url
    };
  }
}
